import { createSlice, createAsyncThunk, miniSerializeError, SerializedError } from '@reduxjs/toolkit';
import authService from 'src/services/authService';
import featureFlagService, { FeatureFlagsPayload } from 'src/services/featureFlagService';
import { LoadingStates } from '../types/constants';
import { appendError } from 'src/utils/helpers';

// **** Constants **** //

const ERROR_MESSAGE_PREPENDS = {
  Update: 'Failed to update feature flag:',
} as const;

// **** Types **** //

export type UserState = {
  token: string;
  alias: string;
  departmentCode: string;
  lastName: string;
  firstName: string;
  email: string;
  ldap: string[];
  siteLocation: string;
  preferredSite: number;
  userLoading: LoadingStates;
  authError: SerializedError | null;
  jobLevel: string;
  jobDescription: string;
  hasPermission: boolean;
  featureFlags: {
    PLACEMENT_TRANSFER: boolean;
    DECISION_PORTAL: boolean;
    ATOZ_NOTIFICATIONS: boolean;
    JOB_MODIFICATIONS: boolean;
    CORRESPONDENCE: boolean;
    PERSONA: boolean;
    TASK_GROUPING: boolean;
  };
  pageLastLoadTimestamp: number;
  isLoading: boolean;
  isFeatureFlagsLoaded: boolean;
  isLoadingUpdateFeatureFlag: boolean;
  updateFeatureFlagErrorMessage: string;
};

interface UpdateFeatureFlagPayload {
  featureName: string;
  isEnabled: boolean;
}

// **** Functions **** //

/**
 * Get the authenticated user
 */
export const getUser = createAsyncThunk('user/getUser', async () => {
  return authService.login();
});

/**
 * Fetch the list of feature flags (from the back-end environment properties)
 */
export const getFeatureFlags = createAsyncThunk<FeatureFlagsPayload>('user/getFeatureFlags', async () => {
  return featureFlagService.getFeatureFlags();
});

/**
 * Update in the back-end whether a particular feature-flag is enable.
 */
export const updateFeatureFlag = createAsyncThunk('user/updateFeatureFlag', (payload: UpdateFeatureFlagPayload) => {
  const { featureName, isEnabled } = payload;
  return featureFlagService.updateFeatureFlag(featureName, isEnabled);
});

// **** Setup **** //

const initialState: UserState = {
  token: '',
  alias: '',
  departmentCode: '',
  lastName: '',
  firstName: '',
  email: '',
  ldap: [],
  siteLocation: '',
  preferredSite: 0,
  userLoading: LoadingStates.Pending,
  authError: null,
  jobLevel: '',
  jobDescription: '',
  hasPermission: true,
  featureFlags: {
    PLACEMENT_TRANSFER: false,
    DECISION_PORTAL: false,
    ATOZ_NOTIFICATIONS: false,
    JOB_MODIFICATIONS: false,
    CORRESPONDENCE: false,
    PERSONA: false,
    TASK_GROUPING: false,
  },
  pageLastLoadTimestamp: 0,
  isLoading: false,
  isFeatureFlagsLoaded: false,
  isLoadingUpdateFeatureFlag: false,
  updateFeatureFlagErrorMessage: '',
};

// UserSlice
const { reducer, actions } = createSlice({
  name: 'user',
  initialState,
  reducers: {
    getUserFailure: (state) => {
      state.userLoading = LoadingStates.Rejected;
    },
    setPreferredSite: (state, action) => {
      state.preferredSite = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ** Fetch "Feature Flags" ** //
    builder.addCase(getFeatureFlags.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getFeatureFlags.fulfilled, (state, { payload }: { payload: FeatureFlagsPayload }) => {
      state.isLoading = false;
      state.featureFlags = {
        PLACEMENT_TRANSFER: payload.placement_transfer,
        DECISION_PORTAL: payload.decision_portal,
        ATOZ_NOTIFICATIONS: payload.atoz_notifications,
        JOB_MODIFICATIONS: payload.job_modifications,
        CORRESPONDENCE: payload.correspondence,
        PERSONA: payload.persona,
        TASK_GROUPING: payload.task_grouping,
      };
      state.isFeatureFlagsLoaded = true;
    });
    builder.addCase(getFeatureFlags.rejected, (state) => {
      state.isLoading = false;
      state.isFeatureFlagsLoaded = true;
    });
    // ** Update "Feature Flag" ** //
    builder.addCase(updateFeatureFlag.pending, (state) => {
      state.isLoadingUpdateFeatureFlag = true;
    });
    builder.addCase(updateFeatureFlag.fulfilled, (state, { payload }: { payload: FeatureFlagsPayload }) => {
      state.isLoadingUpdateFeatureFlag = false;
      state.featureFlags = {
        PLACEMENT_TRANSFER: payload.placement_transfer,
        DECISION_PORTAL: payload.decision_portal,
        ATOZ_NOTIFICATIONS: payload.atoz_notifications,
        JOB_MODIFICATIONS: payload.job_modifications,
        CORRESPONDENCE: payload.correspondence,
        PERSONA: payload.persona,
        TASK_GROUPING: payload.task_grouping,
      };
      state.updateFeatureFlagErrorMessage = '';
    });
    builder.addCase(updateFeatureFlag.rejected, (state, { error }) => {
      state.isLoadingUpdateFeatureFlag = false;
      state.updateFeatureFlagErrorMessage = appendError(ERROR_MESSAGE_PREPENDS.Update, error);
    });
    // ** Fetch "User" ** //
    builder.addCase(getUser.pending, (state) => {
      state.userLoading = LoadingStates.Pending;
    });
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      const { alias, token, email, firstName, lastName, siteLocation, ldap, departmentCode, jobLevel, jobDescription } =
        payload;
      state.token = token;
      state.alias = alias;
      state.email = email;
      state.firstName = firstName;
      state.lastName = lastName;
      state.siteLocation = siteLocation;
      state.ldap = ldap;
      state.userLoading = LoadingStates.Fulfilled;
      state.departmentCode = departmentCode;
      state.jobLevel = jobLevel;
      state.jobDescription = jobDescription;
      state.pageLastLoadTimestamp = Date.now();
      if (state.ldap.length === 0) {
        state.hasPermission = false;
      }
      // TODO: add JWT token to api header
    });
    builder.addCase(getUser.rejected, (state, { error }) => {
      state.authError = miniSerializeError(error);
      state.userLoading = LoadingStates.Rejected;
      state.hasPermission = false;
    });
  },
});

// **** Export **** //

export const { setPreferredSite } = actions;
export default reducer;
